/**
 * Organization-level white-label branding context.
 * Fetches branding after login, applies theme via CSS variables only (no raw CSS injection).
 * Fallback: SignTada defaults when no branding is set.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from './config';
import logger from './utils/logger';

const DEFAULT_BRANDING = {
  product_name: 'SignTada',
  logo_url: null,
  theme: 'default',
  primary_color: '#FFD600',
  accent_color: '#FFAB00',
  show_powered_by: true,
  first_part_color: null,
  rest_part_color: null,
  custom_themes: [],
};

const BRANDING_LOGO_STORAGE_KEY = 'branding_logo_url';

function getStoredLogoUrl() {
  try {
    return sessionStorage.getItem(BRANDING_LOGO_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function setStoredLogoUrl(url) {
  try {
    if (url) sessionStorage.setItem(BRANDING_LOGO_STORAGE_KEY, url);
    else sessionStorage.removeItem(BRANDING_LOGO_STORAGE_KEY);
  } catch (_) {}
}

const BrandingContext = createContext(null);

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Only set brand color variables; do not set background/text (handled by ThemeContext + Global.css)
  const applyBrandingToDom = useCallback((b) => {
    if (!b || !b.primary_color) return;
    const root = document.documentElement;
    root.style.setProperty('--primary', b.primary_color);
    root.style.setProperty('--primary-dark', b.accent_color || b.primary_color);
    root.style.setProperty('--accent-color', b.accent_color || b.primary_color);
  }, []);

  const fetchBranding = useCallback(async (orgId = null) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setBranding(DEFAULT_BRANDING);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = orgId
        ? `${API_ENDPOINTS.BRANDING}?orgId=${encodeURIComponent(orgId)}`
        : API_ENDPOINTS.BRANDING;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = res.data;
      const data =
        raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
      let logoUrl = data.hasOwnProperty('logo_url') ? (data.logo_url ?? null) : DEFAULT_BRANDING.logo_url;
      if (logoUrl) setStoredLogoUrl(logoUrl);
      else logoUrl = getStoredLogoUrl();
      const next = {
        product_name: data.product_name ?? DEFAULT_BRANDING.product_name,
        logo_url: logoUrl,
        theme: data.theme ?? DEFAULT_BRANDING.theme,
        primary_color: data.primary_color ?? DEFAULT_BRANDING.primary_color,
        accent_color: data.accent_color ?? DEFAULT_BRANDING.accent_color,
        show_powered_by: data.show_powered_by !== false,
        first_part_color: data.first_part_color ?? null,
        rest_part_color: data.rest_part_color ?? null,
        custom_themes: Array.isArray(data.custom_themes) ? data.custom_themes : [],
      };
      setBranding(next);
      applyBrandingToDom(next);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load branding');
      setBranding(DEFAULT_BRANDING);
      applyBrandingToDom(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, [applyBrandingToDom]);

  const refreshBranding = useCallback((orgId = null) => {
    return fetchBranding(orgId);
  }, [fetchBranding]);

  const updateBranding = useCallback(async (payload, orgId = null) => {
    setError(null);
    try {
      const body = orgId ? { ...payload, orgId } : payload;
      const res = await axios.post(API_ENDPOINTS.BRANDING_UPDATE, body);
      const data = res.data?.branding || res.data || {};
      setBranding(prev => {
        const next = {
          product_name: data.product_name ?? prev.product_name ?? DEFAULT_BRANDING.product_name,
          logo_url: data.logo_url !== undefined ? data.logo_url : prev.logo_url,
          theme: data.theme ?? prev.theme ?? DEFAULT_BRANDING.theme,
          primary_color: data.primary_color ?? prev.primary_color ?? DEFAULT_BRANDING.primary_color,
          accent_color: data.accent_color ?? prev.accent_color ?? DEFAULT_BRANDING.accent_color,
          show_powered_by: data.show_powered_by !== undefined ? data.show_powered_by : prev.show_powered_by,
          first_part_color: data.first_part_color !== undefined ? data.first_part_color : prev.first_part_color,
          rest_part_color: data.rest_part_color !== undefined ? data.rest_part_color : prev.rest_part_color,
          custom_themes: Array.isArray(data.custom_themes) ? data.custom_themes : (prev.custom_themes || []),
        };
        if (next.logo_url) setStoredLogoUrl(next.logo_url);
        else setStoredLogoUrl(null);
        applyBrandingToDom(next);
        return next;
      });
    } catch (err) {
      const msg =
        (err.response?.data && (err.response.data.error ?? err.response.data.message ?? (typeof err.response.data === 'string' ? err.response.data : null)))
        || (err.response?.status === 401 ? 'Session expired. Please log in again.' : null)
        || 'Failed to update branding';
      setError(msg);
      throw new Error(msg);
    }
  }, [applyBrandingToDom]);

  // Initial fetch when token is present (e.g. after login or on reload)
  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const value = {
    branding,
    loading,
    error,
    refreshBranding,
    updateBranding,
    applyBrandingToDom,
    defaultBranding: DEFAULT_BRANDING,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export default BrandingContext;
